import { InputBase, InputBaseComponentProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { v4 } from 'uuid';

import { runWhenReady } from '@/utility/runWhenReady';

interface Props {
  readonly value: readonly string[];
  readonly error?: boolean;
  readonly digits?: number;
  readonly size?: 'small' | 'medium' | 'large';

  onChange(value: readonly string[]): void;
}

export const OneTimePasswordInput: React.FC<Props> = (props: Props): React.ReactElement => {
  const { value, digits = 4, error, size = 'medium', onChange } = props;

  const [container, setContainer] = React.useState<HTMLDivElement | null>(null);

  const focusFirst = React.useCallback((): void => {
    const inputs = container?.querySelectorAll('input');
    if (inputs && inputs.length > 0) {
      focusNow(inputs[0] as HTMLInputElement);
    }
  }, [container]);

  const focusNext = React.useCallback((): void => {
    const activeInput = document.activeElement;
    if (activeInput instanceof HTMLInputElement) {
      const inputs = Array.from(container?.querySelectorAll('input') ?? []);
      const currentInputIndex = inputs.findIndex(
        (input: Element): boolean => input === activeInput,
      );
      if (currentInputIndex === -1) {
        return;
      }
      const nextInput = inputs[currentInputIndex + 1];
      if (nextInput instanceof HTMLInputElement) {
        focusNow(nextInput);
      }
    }
  }, [container]);

  const focusPrev = React.useCallback((): void => {
    const activeInput = document.activeElement;
    if (activeInput instanceof HTMLInputElement) {
      const inputs = Array.from(container?.querySelectorAll('input') ?? []);
      const currentInputIndex = inputs.findIndex(
        (input: Element): boolean => input === activeInput,
      );
      if (currentInputIndex === -1) {
        return;
      }
      const prevInput = inputs[currentInputIndex - 1];
      if (prevInput instanceof HTMLInputElement) {
        focusNow(prevInput);
      }
    }
  }, [container]);

  const backspaceHandler = React.useCallback(
    (index: number): React.KeyboardEventHandler => {
      return (event: React.KeyboardEvent): void => {
        if (event.key !== 'Backspace') {
          return;
        }
        event.stopPropagation();
        event.preventDefault();

        onChange(value.map((digit: string, i: number): string => (i === index ? '' : digit)));
        focusPrev();
      };
    },
    [value, focusPrev, onChange],
  );

  const digitUpdater = React.useCallback(
    (index: number) =>
      (event: React.ChangeEvent<HTMLInputElement>): void => {
        event.preventDefault();
        event.stopPropagation();

        const { value: textValue } = event.target;

        const numeric = Number(textValue);
        if (isNaN(numeric) || numeric < 0 || numeric > 9) {
          return;
        }

        onChange(
          value.map((digit: string, i: number): string => (i === index ? textValue : digit)),
        );
        focusNext();
      },
    [value, focusNext, onChange],
  );

  const onPaste = React.useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>): void => {
      const { clipboardData } = event;

      event.stopPropagation();
      event.preventDefault();

      const content = clipboardData.getData('text');
      if (content.length !== digits) {
        return;
      }

      const value = content.split('');
      if (value.some(isNotNumeric)) {
        return;
      }
      onChange(value);
      focusFirst();
    },
    [digits, focusFirst, onChange],
  );

  const digitKeys = React.useMemo((): readonly string[] => {
    return Array.from({ length: digits }, (_: number, i: number): string => `digit-${i}`);
  }, [digits]);

  return (
    <Box ref={setContainer} display="flex" gap={2}>
      {digitKeys.map((digitId: string, index: number): React.ReactElement => {
        const inputProps: InputBaseComponentProps = {
          sx: [
            styles.input,
            error ? styles.error : undefined,
            { fontSize: fontSizes[size], lineHeight: lineHeights[size], height: lineHeights[size] },
          ],
          'aria-label': digitId,
          maxLength: 1,
        };

        const name = `digit-${digitId}.${v4()}`;

        return (
          <Box key={digitId} flex={1}>
            <InputBase
              name={name}
              autoComplete="newpassword"
              inputProps={inputProps}
              value={value[index]}
              onPasteCapture={onPaste}
              onKeyDownCapture={backspaceHandler(index)}
              onChange={digitUpdater(index)}
            />
          </Box>
        );
      })}
    </Box>
  );
};

const styles: Record<string, SystemStyleObject<Theme>> = {
  error: {
    borderColor: 'error.main',
  },
  input: {
    borderColor: 'custom.DividerLight',
    borderStyle: 'solid',
    backgroundColor: 'custom.InputsBackground',
    borderRadius: '16px',
    borderWidth: 1,
    // TODO: this doesn't feel right, but it works. We should research a better
    //       way to do this.
    '&:focus': {
      borderColor: 'custom.DividerMed',
    },

    textAlign: 'center',
  },
};

const isNotNumeric = (digit: string): boolean => isNaN(Number(digit));

const focusNow = (input: HTMLInputElement): void => {
  runWhenReady((): void => {
    input.focus();
  });
};

const fontSizes: Record<string, number> = {
  small: 40,
  medium: 56,
  large: 64,
};

const lineHeights: Record<string, Record<'sm' | 'xs', string>> = {
  small: { sm: '72px', xs: '80px' },
  medium: { sm: '94px', xs: '115px' },
  large: { sm: '140px', xs: '125px' },
};
